import { formatDateTime } from '@/utils';
import type { SeatSyncState } from '../types';

export type SyncTone = 'ok' | 'warning' | 'danger';

export interface SyncStatusText {
	tone: SyncTone;
	title: string;
	description: string;
	/** Shown as an app-wide banner (only when something needs attention). */
	alert: boolean;
}

const plural = (count: number, noun: string) =>
	`${count} ${noun}${count === 1 ? '' : 's'}`;

/**
 * What an on-prem admin needs to know about the seat sync, worded for them.
 * Running displays keep playing in every state: only new registrations stop.
 */
export function describeSyncStatus(state: SeatSyncState): SyncStatusText {
	const lastSync = state.lastSyncAt
		? `Last synced ${formatDateTime(state.lastSyncAt)}.`
		: 'Never synced.';
	switch (state.status) {
		case 'active':
			return {
				tone: 'ok',
				title: 'Seats granted by VEEP',
				description: lastSync,
				alert: false,
			};
		case 'offline':
			return {
				tone: 'warning',
				title: 'Cannot reach VEEP',
				description: `${lastSync} Everything keeps working${
					state.daysLeft !== null
						? `; new displays can register for ${plural(state.daysLeft, 'more day')}`
						: ''
				}. Check the server's connection to VEEP.`,
				alert: true,
			};
		case 'over_limit':
			return {
				tone: 'warning',
				title: `Remove ${plural(state.overLimitBy, 'display')}`,
				description: `VEEP now grants fewer seats than displays use. Remove ${plural(state.overLimitBy, 'display')}${
					state.overLimitDeadline
						? ` before ${formatDateTime(state.overLimitDeadline)}`
						: ''
				}, or displays picked at random lose their seat. New displays cannot register meanwhile.`,
				alert: true,
			};
		case 'suspended':
			return {
				tone: 'danger',
				title: 'Workspace suspended by VEEP',
				description:
					'Displays keep playing, but new displays cannot register. Contact VEEP.',
				alert: true,
			};
		case 'expired':
			return {
				tone: 'danger',
				title: 'Not synced with VEEP for too long',
				description: `${lastSync} Displays keep playing, but new displays cannot register until the server syncs with VEEP again.`,
				alert: true,
			};
		case 'not_yet_valid':
		case 'clock_tampered':
			return {
				tone: 'danger',
				title: 'Server clock is wrong',
				description:
					'New displays cannot register until the server clock is correct (NTP). Displays keep playing.',
				alert: true,
			};
		case 'not_activated':
			return {
				tone: 'danger',
				title: 'Server not activated with VEEP',
				description:
					'New displays cannot register yet. The deployment team activates the server with a token from VEEP.',
				alert: true,
			};
	}
}
